import React, { useState, useEffect } from 'react';
import { Search, FileSpreadsheet, AlertTriangle, Loader2, Calendar } from 'lucide-react';

const LoanPortfolioView = ({ isDark, onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' });
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loansData, setLoansData] = useState([]);
  const [filteredLoans, setFilteredLoans] = useState([]);
  const [groups, setGroups] = useState([]);

  // Get current month as default
  useEffect(() => {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(currentMonth);
  }, []);

  useEffect(() => {
    fetchLoansData();
  }, [selectedMonth, customDateRange]);

  useEffect(() => {
    applyFilters();
  }, [loansData, searchQuery, selectedGroup]);

  const fetchLoansData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Mock API call - replace with actual API
      // const response = await metricsAPI.getLoansPortfolio({
      //   month: selectedMonth,
      //   startDate: customDateRange.startDate,
      //   endDate: customDateRange.endDate,
      //   status: 'disbursed'  // Only fetch disbursed loans
      // });

      // Mock data for demonstration - Only disbursed loans
      const mockLoans = [
        {
          loan_id: 'LN001',
          group_name: 'Jitegemee Group',
          member_name: 'Jane Wanjiru',
          loan_amount: 50000,
          duration: 12,
          interest_rate: 10,
          total_repayment: 55000,
          officer: 'John Kamau',
          status: 'disbursed'
        },
        {
          loan_id: 'LN005',
          group_name: 'Unity Group',
          member_name: 'Sarah Wambui',
          loan_amount: 60000,
          duration: 12,
          interest_rate: 10,
          total_repayment: 66000,
          officer: 'Mary Njeri',
          status: 'disbursed'
        },
        {
          loan_id: 'LN008',
          group_name: 'Progress Group',
          member_name: 'James Omondi',
          loan_amount: 40000,
          duration: 6,
          interest_rate: 10,
          total_repayment: 44000,
          officer: 'Sarah Akinyi',
          status: 'disbursed'
        },
        {
          loan_id: 'LN012',
          group_name: 'Hope Group',
          member_name: 'Grace Muthoni',
          loan_amount: 35000,
          duration: 9,
          interest_rate: 10,
          total_repayment: 38500,
          officer: 'John Kamau',
          status: 'disbursed'
        }
      ];

      const mockGroups = ['Jitegemee Group', 'Unity Group', 'Progress Group', 'Hope Group'];

      // Filter to only include disbursed loans
      const disbursedLoans = mockLoans.filter(loan => loan.status === 'disbursed');

      setLoansData(disbursedLoans);
      setGroups(mockGroups);
      setFilteredLoans(disbursedLoans);
    } catch (err) {
      setError('Failed to load loan portfolio data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...loansData];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(loan =>
        loan.member_name.toLowerCase().includes(query) ||
        loan.loan_id.toLowerCase().includes(query) ||
        loan.group_name.toLowerCase().includes(query)
      );
    }

    // Group filter
    if (selectedGroup !== 'all') {
      filtered = filtered.filter(loan => loan.group_name === selectedGroup);
    }

    setFilteredLoans(filtered);
  };

  const calculateTotals = () => {
    return filteredLoans.reduce((acc, loan) => {
      acc.totalLoanAmount += loan.loan_amount;
      acc.totalRepayment += loan.total_repayment;
      acc.totalInterest += (loan.total_repayment - loan.loan_amount);
      return acc;
    }, { totalLoanAmount: 0, totalRepayment: 0, totalInterest: 0 });
  };

  const calculateInterest = (loan) => {
    return loan.total_repayment - loan.loan_amount;
  };

  const getDisplayPeriod = () => {
    if (showCustomDate && customDateRange.startDate && customDateRange.endDate) {
      return `${new Date(customDateRange.startDate).toLocaleDateString()} - ${new Date(customDateRange.endDate).toLocaleDateString()}`;
    }
    if (selectedMonth) {
      const [year, month] = selectedMonth.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return '';
  };

  const exportToExcel = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  const clearFilters = () => {
    setSelectedGroup('all');
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading loan portfolio...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button 
          onClick={fetchLoansData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const totals = calculateTotals();

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Loan Portfolio - Disbursed Loans
            </h2>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Showing disbursed loans for: <span className="font-semibold">{getDisplayPeriod()}</span>
            </p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                isDark
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              ← Back to Book Portfolio
            </button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-green-400' : 'text-green-600'}`}>Total Disbursed Loans</div>
            <div className="text-2xl font-bold text-green-600">{loansData.length}</div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Total Principal</div>
            <div className="text-2xl font-bold text-blue-600">
              KSh {totals.totalLoanAmount.toLocaleString()}
            </div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-purple-900/20 border border-purple-800' : 'bg-purple-50 border border-purple-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Total Interest</div>
            <div className="text-2xl font-bold text-purple-600">
              KSh {totals.totalInterest.toLocaleString()}
            </div>
          </div>
          <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Expected</div>
            <div className="text-2xl font-bold text-emerald-600">
              KSh {totals.totalRepayment.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Search and Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
          {/* Search Bar */}
          <div className="md:col-span-5 relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder="Search member, loan ID, group..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                isDark 
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>

          {/* Group Filter */}
          <div className="md:col-span-3">
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200'
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="all">All Groups</option>
              {groups.map((group, idx) => (
                <option key={idx} value={group}>{group}</option>
              ))}
            </select>
          </div>

          {/* Month Selector */}
          <div className="md:col-span-2">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setShowCustomDate(false);
              }}
              disabled={showCustomDate}
              className={`w-full px-3 py-2 border rounded-lg ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200'
                  : 'bg-white border-gray-300 text-gray-900'
              } focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50`}
            />
          </div>

          {/* Export Button */}
          <div className="md:col-span-2">
            <button
              onClick={exportToExcel}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FileSpreadsheet className="w-5 h-5" />
              Export
            </button>
          </div>
        </div>

        {/* Custom Date Range */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowCustomDate(!showCustomDate)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showCustomDate
                ? 'bg-blue-600 text-white border-blue-600'
                : isDark
                ? 'bg-[#2b323c] border-gray-700 text-gray-300 hover:bg-gray-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Calendar className="w-4 h-4" />
            {showCustomDate ? 'Using Custom Range' : 'Use Custom Date Range'}
          </button>

          {showCustomDate && (
            <>
              <input
                type="date"
                value={customDateRange.startDate}
                onChange={(e) => setCustomDateRange({ ...customDateRange, startDate: e.target.value })}
                placeholder="Start Date"
                className={`px-3 py-2 border rounded-lg ${
                  isDark
                    ? 'bg-[#2b323c] border-gray-700 text-gray-200'
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>to</span>
              <input
                type="date"
                value={customDateRange.endDate}
                onChange={(e) => setCustomDateRange({ ...customDateRange, endDate: e.target.value })}
                placeholder="End Date"
                className={`px-3 py-2 border rounded-lg ${
                  isDark
                    ? 'bg-[#2b323c] border-gray-700 text-gray-200'
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </>
          )}

          {(selectedGroup !== 'all' || searchQuery) && (
            <button
              onClick={clearFilters}
              className={`ml-auto px-4 py-2 rounded-lg text-sm ${
                isDark
                  ? 'text-gray-400 hover:text-gray-200'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Loans Table */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Loan ID
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Group Name
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Member Name
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Principal
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Duration
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Interest
                </th>
                <th className={`px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total Repayment
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Officer
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {filteredLoans.map((loan, index) => (
                <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4">
                    <span className={`font-mono text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      {loan.loan_id}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                      {loan.group_name}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {loan.member_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-semibold text-blue-600">
                      KSh {loan.loan_amount.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {loan.duration} months
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-semibold text-purple-600">
                      KSh {calculateInterest(loan).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-semibold text-green-600">
                      KSh {loan.total_repayment.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {loan.officer}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={`border-t-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <tr>
                <td className={`px-6 py-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} colSpan="3">
                  TOTAL ({filteredLoans.length} loans)
                </td>
                <td className="px-6 py-4 text-right font-bold text-blue-600">
                  KSh {totals.totalLoanAmount.toLocaleString()}
                </td>
                <td></td>
                <td className="px-6 py-4 text-right font-bold text-purple-600">
                  KSh {totals.totalInterest.toLocaleString()}
                </td>
                <td className="px-6 py-4 text-right font-bold text-green-600">
                  KSh {totals.totalRepayment.toLocaleString()}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {filteredLoans.length === 0 && (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
          <p className="text-lg">No disbursed loans found matching your criteria.</p>
          <button
            onClick={clearFilters}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
};

export default LoanPortfolioView;